import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');
export const getUsers = () => api.get('/users');
export const toggleUser = (userId) => api.put(`/users/${userId}/toggle`);

// Tenant
export const registerTenant = (data) => api.post('/tenants/register', data);
export const getTrialStatus = () => api.get('/tenants/trial-status');

// Clientes
export const getClientes = (params) => api.get('/clientes', { params });
export const getCliente = (id) => api.get(`/clientes/${id}`);
export const createCliente = (data) => api.post('/clientes', data);
export const updateCliente = (id, data) => api.put(`/clientes/${id}`, data);
export const updateClienteEstado = (id, estado) => api.put(`/clientes/${id}/estado?estado=${estado}`);

// Prestamos
export const getPrestamos = (params) => api.get('/prestamos', { params });
export const getPrestamo = (id) => api.get(`/prestamos/${id}`);
export const createPrestamo = (data) => api.post('/prestamos', data);
export const renovarPrestamo = (id, data) => api.post(`/prestamos/${id}/renovar`, data);
export const getHistorialRenovaciones = (id) => api.get(`/prestamos/${id}/historial-renovaciones`);

// Pagos
export const getPagos = (params) => api.get('/pagos', { params });
export const createPago = (data) => api.post('/pagos', data);

// Rutas
export const getRutas = () => api.get('/rutas');
export const createRuta = (data) => api.post('/rutas', data);
export const asignarCobrador = (rutaId, cobradorId) => api.put(`/rutas/${rutaId}/asignar/${cobradorId}`);

// Caja
export const cierreCaja = (data) => api.post('/caja/cierre', data);
export const getCierres = (params) => api.get('/caja/cierres', { params });

// Reportes
export const getDashboard = () => api.get('/reportes/dashboard');
export const getReporteMorosidad = () => api.get('/reportes/morosidad');
export const getReporteIngresos = (periodo) => api.get(`/reportes/ingresos?periodo=${periodo}`);

// Configuracion
export const getConfiguracion = () => api.get('/configuracion');
export const updateConfiguracion = (data) => api.put('/configuracion', data);

// Notificaciones WhatsApp
export const enviarNotificacion = (data) => api.post('/notificaciones/enviar', data);
export const enviarNotificacionesMasivas = (tipo, diasAntes = 1) => 
    api.post(`/notificaciones/enviar-masivo?tipo=${tipo}&dias_antes=${diasAntes}`);
export const getHistorialNotificaciones = (clienteId, limit = 100) => 
    api.get('/notificaciones/historial', { params: { cliente_id: clienteId, limit } });

// Empleados
export const getEmpleados = (params) => api.get('/empleados', { params });
export const getEmpleado = (id) => api.get(`/empleados/${id}`);
export const createEmpleado = (data) => api.post('/empleados', data);
export const updateEmpleado = (id, data) => api.put(`/empleados/${id}`, data);
export const updateEmpleadoEstado = (id, estado) => api.put(`/empleados/${id}/estado?estado=${estado}`);

// Nómina
export const calcularNomina = (data) => api.post('/nomina/calcular', data);
export const pagarNomina = (id) => api.put(`/nomina/${id}/pagar`);
export const getNominas = (params) => api.get('/nomina', { params });
export const getResumenNomina = (periodo) => api.get(`/nomina/resumen/${periodo}`);

// Gastos / Contabilidad
export const createGasto = (data) => api.post('/gastos', data);
export const getGastos = (params) => api.get('/gastos', { params });
export const getReporteGastos = (mes) => api.get('/gastos/reporte', { params: { mes } });

// Simulador
export const simularPrestamo = (data) => api.post('/simulador/prestamo', data);

// Análisis de Crédito
export const analizarCliente = (clienteId, data) => api.post(`/analisis/cliente/${clienteId}`, data);
export const getHistorialAnalisis = (clienteId) => api.get(`/analisis/cliente/${clienteId}/historial`);
export const getEstadisticasAnalisis = () => api.get('/analisis/estadisticas');

// Solicitudes de Préstamo
export const crearSolicitud = (data) => api.post('/solicitudes', data);
export const getSolicitudes = (params) => api.get('/solicitudes', { params });
export const getSolicitud = (id) => api.get(`/solicitudes/${id}`);
export const aprobarSolicitud = (id, data) => api.post(`/solicitudes/${id}/aprobar`, data);
export const convertirSolicitud = (id) => api.post(`/solicitudes/${id}/convertir`);

// Exportar Excel
export const exportCarteraExcel = () => api.get('/export/cartera', { responseType: 'blob' });
export const exportMorosidadExcel = () => api.get('/export/morosidad', { responseType: 'blob' });
export const exportPagosExcel = (params) => api.get('/export/pagos', { params, responseType: 'blob' });
export const exportNominaExcel = (periodo) => api.get(`/export/nomina/${periodo}`, { responseType: 'blob' });

// Dashboard Cobrador
export const getCobradorDashboard = () => api.get('/cobrador/dashboard');
export const marcarVisita = (data) => api.post('/cobrador/marcar-visita', null, { params: data });
export const getHistorialVisitas = (fecha) => api.get('/cobrador/historial-visitas', { params: { fecha } });

// Scheduler
export const ejecutarRecordatorios = () => api.post('/scheduler/ejecutar-recordatorios');
export const ejecutarMora = () => api.post('/scheduler/ejecutar-mora');

// Audit
export const getAuditLog = (limit) => api.get(`/audit?limit=${limit}`);

// Seed
export const seedData = () => api.post('/seed');

// PDF URLs
export const getReciboPdfUrl = (pagoId) => `${API_URL}/api/pdf/recibo/${pagoId}`;
export const getContratoPdfUrl = (prestamoId) => `${API_URL}/api/pdf/contrato/${prestamoId}`;
export const getPagarePdfUrl = (prestamoId) => `${API_URL}/api/pdf/pagare/${prestamoId}`;
export const getContratoEmpleadoPdfUrl = (empleadoId) => `${API_URL}/api/pdf/contrato-empleado/${empleadoId}`;

export default api;
